import { ENV } from '@/core/config';
import { translate } from '@/i18n';

const formatErrorObject = (error) =>
  Object.keys(error)
    .map((key) => {
      const val = error[key];
      const formattedVal =
        typeof val === 'object'
          ? formatErrorObject(val)
          : typeof val === 'string'
            ? translate(val)
            : val;
      const translatedKey = translate(key);
      const displayKey =
        isNaN(Number(key))
          ? translatedKey !== key
            ? translatedKey
            : translate(key.charAt(0).toUpperCase() + key.slice(1)) !==
                key.charAt(0).toUpperCase() + key.slice(1)
              ? translate(key.charAt(0).toUpperCase() + key.slice(1))
              : key
          : key;
      return isNaN(Number(key)) ? `${displayKey}: ${formattedVal}` : formattedVal;
    })
    .filter(Boolean)
    .join(', ');

export const format = (error, parseResponse?) => {
  /*
  Empty response or status code -1 denotes network error.
  Usually it is caused by one of the following reasons:

  - client is not connected to the internet;
  - server is down;
  - server is inaccessible via VPN;
  - server is located in another domain and didn't return valid CORS headers;
  - request has timed out;
  - request was aborted, ie manually cancelled by the client.

  See also: https://fetch.spec.whatwg.org/#concept-filtered-response
  */

  if (Object.prototype.hasOwnProperty.call(error, 'message')) {
    return error.message;
  }

  if (!error || error.status === -1) {
    return translate(
      'Unfortunately, connection to server has failed. Please check if you can connect to {apiEndpoint} from your browser and contact support if the error continues.',
      { apiEndpoint: ENV.apiEndpoint },
    );
  }

  if (!Object.prototype.hasOwnProperty.call(error, 'status')) {
    if (typeof error === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { response, ...errorRest } = error;
      return formatErrorObject(errorRest);
    } else {
      return error;
    }
  }

  if (error.response && error.response.status === 413) {
    return translate('File too large. Please select a smaller file.');
  }

  let message = `${error.status}: ${error.statusText}.`;

  const errorData = error.data ?? error.response?.data;

  if (errorData) {
    if (parseResponse) {
      message += ' ' + parseResponse(error, message);
    }
    if (errorData.non_field_errors) {
      const nfe = Array.isArray(errorData.non_field_errors)
        ? errorData.non_field_errors
            .map((item) => (typeof item === 'string' ? translate(item) : item))
            .join('. ')
        : typeof errorData.non_field_errors === 'string'
          ? translate(errorData.non_field_errors)
          : errorData.non_field_errors;
      message += ' ' + nfe;
    } else if (errorData.detail) {
      message +=
        ' ' +
        (typeof errorData.detail === 'string'
          ? translate(errorData.detail)
          : errorData.detail);
    } else if (Array.isArray(errorData)) {
      message +=
        ' ' +
        errorData
          .map((item) => {
            if (typeof item === 'object') {
              return formatErrorObject(item);
            } else {
              return typeof item === 'string' ? translate(item) : item;
            }
          })
          .join('. ');
    } else if (typeof errorData === 'object') {
      message += ' ' + formatErrorObject(errorData);
    }
  }

  return message;
};
